<template>
  <div class="order-form">
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('orderDetails') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('supplier')"
          label-for="supplier"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            class="multiselect-supplier"
            :placeholder="$t('supplier') + ' ' + $t('select') + '/suchen'"
            v-model="localOrder.supplier"
            :options="filteredSuppliers"
            track-by="_id"
            :allow-empty="false"
            :deselectLabel="$t('deselectLabel')"
            selectLabel=""
            selectedLabel=""
            :internal-search="false"
            :close-on-select="true"
            :clear-on-select="false"
            :multiple="false"
            :show-labels="true"
            @search-change="onSupplierSearch"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <div v-if="option">
                {{ option.name }}
              </div>
            </template>
            <template slot="option" slot-scope="{ option }">
              <div>{{ option.name }}</div>
            </template>
            <template slot="noResult">
              <div>{{ $t('no_results') }}</div>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('supplierOrderNumber')"
          label-for="supplierOrderNumber"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="localOrder.supplierOrderNumber"
            type="text"
            :placeholder="$t('supplierOrderNumber')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('orderState')"
          label-for="orderState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('orderState') + ' ' + $t('select')"
            v-model="localOrder.orderState"
            :options="getEnumValues('OrderState')"
            :show-labels="true"
            :allow-empty="false"
            :taggable="true"
            :selectLabel="$t('select')"
            :selectedLabel="$t('selected')"
            :deselectLabel="$t('remove_selection')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="getOrderStateColor(option)">{{ $t(option) }}</b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="getOrderStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('orderType')"
          label-for="orderType"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('orderType') + ' ' + $t('select')"
            v-model="localOrder.orderType"
            :options="getEnumValues('OrderType')"
            :show-labels="true"
            :allow-empty="false"
            :taggable="true"
            :selectLabel="$t('select')"
            :selectedLabel="$t('selected')"
            :deselectLabel="$t('remove_selection')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="getOrderTypeColor(option)">{{ $t(option) }}</b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="getOrderTypeColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('submittedToSupplierDate')"
          label-for="submittedToSupplierDate"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="submittedToSupplierDate"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_and_time')"
            :showTime="true"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2020:2030"
            hourFormat="24"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('expectedDeliveryDate')"
          label-for="expectedDeliveryDate"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="expectedDeliveryDate"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2020:2030"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('actualDeliveryDate')"
          label-for="actualDeliveryDate"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="actualDeliveryDate"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2020:2030"
          />
        </b-form-group>
      </b-col>
      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('basicData') }}</strong>
        </legend>

        <b-form-group
          v-if="!projectContext"
          horizontal
          :label="$t('project')"
          label-for="project"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            class="multiselect-project"
            :placeholder="$t('filter_by_lastname')"
            v-model="project"
            :options="filteredProjects"
            :allow-empty="true"
            track-by="number"
            :deselectLabel="$t('deselectLabel')"
            selectLabel=""
            selectedLabel=""
            :internal-search="false"
            :show-labels="true"
            @search-change="onProjectSearch"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <div>
                #{{ option.number.toString().padStart(4, '0') }} - {{ option.customer.lastname }},
                {{ option.customer.firstname }}
              </div>
              <div>
                {{ option.customer.street }} {{ option.customer.streetNumber
                }}<span v-if="option.customer.streetNumberSuffix">{{
                  option.customer.streetNumberSuffix
                }}</span
                >, {{ option.customer.zip }}
                {{ option.customer.city }}
              </div>
            </template>
            <template slot="option" slot-scope="{ option }">
              <div>
                #{{ option.number.toString().padStart(4, '0') }}: {{ option.customer.lastname }},
                {{ option.customer.firstname }}
              </div>
              <div>
                {{ option.customer.street }} {{ option.customer.streetNumber }},
                {{ option.customer.streetNumberSuffix }}, {{ option.customer.zip }}
                {{ option.customer.city }}
              </div>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('priority')"
          label-for="priority"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('priority') + ' ' + $t('select')"
            v-model="localOrder.priority"
            :options="getEnumValues('OrderPriority')"
            :show-labels="true"
            :allow-empty="false"
            :taggable="true"
            :selectLabel="$t('select')"
            :selectedLabel="$t('selected')"
            :deselectLabel="$t('remove_selection')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="getOrderPriorityColor(option)">{{ $t(option) }}</b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="getOrderPriorityColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('notes')"
          label-for="notes"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-textarea v-model="localOrder.notes" :placeholder="$t('notes')" rows="3" />
        </b-form-group>

        <b-form-group
          v-if="localOrder.number && localOrder.number !== '_new'"
          horizontal
          :label="$t('order_number')"
          label-for="orderNumber"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          class="mb-0"
        >
          <div class="form-control-plaintext">
            {{ localOrder.number.toString().padStart(4, '0') }}
          </div>
        </b-form-group>

        <b-form-group
          v-if="localOrder.createdBy"
          horizontal
          :label="$t('createdBy')"
          label-for="createdBy"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          class="mb-0"
        >
          <div class="form-control-plaintext">
            {{ localOrder.createdBy.firstname }} {{ localOrder.createdBy.lastname
            }}<span v-if="localOrder.createdAt"
              >, {{ $dayjs(localOrder.createdAt).format('DD.MM.YYYY HH:mm') }}</span
            >
          </div>
        </b-form-group>

        <b-form-group
          v-if="localOrder.updatedBy"
          horizontal
          :label="$t('updatedBy')"
          label-for="updatedBy"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          class="mb-0"
        >
          <div class="form-control-plaintext">
            {{ localOrder.updatedBy.firstname }} {{ localOrder.updatedBy.lastname
            }}<span v-if="localOrder.updatedAt"
              >, {{ $dayjs(localOrder.updatedAt).format('DD.MM.YYYY HH:mm') }}</span
            >
          </div>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Order Items Section -->
    <b-row>
      <b-col lg="12" md="12" sm="12">
        <div class="order-items-section">
          <div class="section-header mb-2 mt-4" style="border-bottom: 1px solid #dee2e6">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <legend class="mb-0">
                <strong>{{ $t('order_items') }}</strong>
              </legend>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-success ml-2"
                  @click="openItemBrowser"
                  type="button"
                  style="white-space: nowrap; font-size: 0.875rem;"
                >
                  <i class="fa fa-plus mr-1"></i>
                  {{ $t('add_items') }}
                </button>
                <ButtonWait
                  v-if="localOrder.items && localOrder.items.length > 0 && !isNew"
                  :startCallback="startOrderExportBuild"
                  :pollCallback="fetchOrderExportBuildState"
                  class="ml-2"
                  style="white-space: nowrap;"
                >
                  <template v-slot:buttonText>
                    {{ $t('generate_order_list') }}
                  </template>
                </ButtonWait>
                <div
                  v-if="
                    localOrder.orderExportAttachments &&
                      localOrder.orderExportAttachments.length > 0
                  "
                  class="export-files-section"
                >
                  <AttachmentList
                    :attachments="localOrder.orderExportAttachments"
                    attachmentType="orderExportAttachments"
                    :inline="true"
                    v-on:attachment-list:delete="onDeleteOrderAttachment"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Current Order Items -->
          <div class="current-items mb-3">
            <OrderItems :items="localOrder.items || []" @update-items="updateOrderItems" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Item Browser Modal -->
    <Dialog
      :header="$t('add_items')"
      :visible.sync="showItemBrowserModal"
      :modal="true"
      :style="{ width: '90vw', maxWidth: '1600px' }"
      :contentStyle="{ height: '70vh', overflow: 'visible' }"
      :closable="true"
    >
      <ItemBrowser
        context="order"
        :show-pricing="true"
        :show-supplier-filter="true"
        default-view-mode="cards"
        @add="addItemToOrder"
      />

      <template #footer>
        <button class="btn btn-default" @click="closeItemBrowser">
          {{ $t('close') }}
        </button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import ItemBrowser from '@/components/ItemBrowser.vue';
import OrderItems from '@/components/OrderItems.vue';
import Dialog from 'primevue/dialog';
import Calendar from 'primevue/calendar';
import ButtonWait from '@/components/ButtonWait.vue';
import { getOrderPriorityColor, getOrderStateColor, getOrderTypeColor } from '@/helpers/colors';
import { FilterMatchMode } from 'primevue/api';
import AttachmentList from '@/components/AttachmentList.vue';

export default {
  name: 'OrderForm',
  components: {
    Multiselect,
    ItemBrowser,
    OrderItems,
    Dialog,
    Calendar,
    ButtonWait,
    AttachmentList,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    projectContext: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      localOrder: { ...this.order },
      isSyncingFromProp: false,
      showItemBrowserModal: false,
      filteredProjects: [],
      filteredSuppliers: [],
      projectsFilter: {
        pagination: {
          page: 0,
          rowsPerPage: 20,
        },
        sortField: 'number',
        sortOrder: -1,
        filterName: 'orders-project-filters',
        filters: {
          number: { value: null, matchMode: FilterMatchMode.EQUALS },
          customerLastname: { value: null, matchMode: FilterMatchMode.CONTAINS },
          customerFirstname: { value: null, matchMode: FilterMatchMode.CONTAINS },
        },
      },
      suppliersFilter: {
        pagination: {
          skip: 0,
          pageSize: 20,
        },
        sorting: {
          sortField: 'number',
          sortOrder: 1,
        },
        filterName: 'orders-supplier-filters',
        filters: {
          number: { value: null, matchMode: FilterMatchMode.EQUALS },
          name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        },
      },
    };
  },
  computed: {
    ...mapGetters(['getEnumValues', 'getProjects', 'getSuppliers', 'getOrderByNumber']),
    ...mapGetters('auth', ['isAdmin', 'isClient', 'getCurrentUser']),

    project: {
      get() {
        return this.projectContext || this.localOrder.project;
      },
      set(value) {
        if (!this.projectContext) {
          this.localOrder.project = value;
          this.emitUpdate();
        }
      },
    },

    submittedToSupplierDate: {
      get() {
        return this.localOrder.submittedToSupplierDate
          ? this.$dayjs(this.localOrder.submittedToSupplierDate).toDate()
          : null;
      },
      set(value) {
        this.localOrder.submittedToSupplierDate = value;
        this.emitUpdate();
      },
    },

    expectedDeliveryDate: {
      get() {
        return this.localOrder.expectedDeliveryDate
          ? this.$dayjs(this.localOrder.expectedDeliveryDate).toDate()
          : null;
      },
      set(value) {
        this.localOrder.expectedDeliveryDate = value;
        this.emitUpdate();
      },
    },

    actualDeliveryDate: {
      get() {
        return this.localOrder.actualDeliveryDate
          ? this.$dayjs(this.localOrder.actualDeliveryDate).toDate()
          : null;
      },
      set(value) {
        this.localOrder.actualDeliveryDate = value;
        this.emitUpdate();
      },
    },

    isNew() {
      return this.localOrder.number === '_new';
    },
  },
  watch: {
    order: {
      handler(newOrder) {
        console.log('OrderForm: order changed', newOrder);
        if (newOrder === this.localOrder) {
          return;
        }
        this.isSyncingFromProp = true;
        this.localOrder = { ...newOrder };
        this.$nextTick(() => {
          this.isSyncingFromProp = false;
        });
      },
      deep: true,
      immediate: true,
    },
    localOrder: {
      handler() {
        if (this.isSyncingFromProp) {
          return;
        }
        this.emitUpdate();
      },
      deep: true,
    },
    projectContext: {
      handler(newProject) {
        if (newProject) {
          this.localOrder.project = newProject;
          this.emitUpdate();
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions([
      'fetchProjectsPaginated',
      'fetchProjectsPaginatedByClient',
      'fetchSuppliersPaginated',
      'fetchUserAttachmentUrl',
      'fetchOrderByNumber',
      'fetchOrderExportAttachmentBuildState',
      'deleteOrderAttachment',
      'fetchEnumValues',
    ]),
    ...mapActions(['buildSingleOrderExport']),
    ...mapActions('auth', ['refreshUser']),

    getOrderPriorityColor,
    getOrderStateColor,
    getOrderTypeColor,

    emitUpdate() {
      this.$emit('update:order', this.localOrder);
    },

    async onProjectSearch(rawQuery) {
      const query = rawQuery.toLowerCase();
      this.projectsFilter.filters.customerLastname.value = query;
      await this.loadProjectData();
    },

    async onSupplierSearch(rawQuery) {
      const query = rawQuery.toLowerCase();
      this.suppliersFilter.filters.name.value = query;
      await this.loadSupplierData();
    },

    async loadProjectData() {
      if (this.isClient) {
        await this.fetchProjectsPaginatedByClient({
          clientId: this.getCurrentUser.client.id,
          page: this.projectsFilter.pagination.page,
          pageSize: this.projectsFilter.pagination.rowsPerPage,
          sortField: this.projectsFilter.sortField,
          sortOrder: this.projectsFilter.sortOrder,
          filters: { ...this.projectsFilter.filters },
        });
      } else if (this.isAdmin) {
        await this.fetchProjectsPaginated({
          page: this.projectsFilter.pagination.page,
          pageSize: this.projectsFilter.pagination.rowsPerPage,
          sortField: this.projectsFilter.sortField,
          sortOrder: this.projectsFilter.sortOrder,
          filters: { ...this.projectsFilter.filters },
        });
      }
      this.filteredProjects = this.getProjects;

      // Ensure current project is in the list
      if (this.localOrder && this.localOrder.project) {
        const projectExists = this.filteredProjects.find(
          (p) => p.number === this.localOrder.project.number
        );
        if (!projectExists) {
          this.filteredProjects.unshift(this.localOrder.project);
        }
      }
    },

    async loadSupplierData() {
      await this.fetchSuppliersPaginated({
        pagination: this.suppliersFilter.pagination,
        sorting: this.suppliersFilter.sorting,
        filters: { ...this.suppliersFilter.filters },
      });
      this.filteredSuppliers = this.getSuppliers;

      // Default to first supplier if none is selected
      if (!this.localOrder.supplier && this.filteredSuppliers.length > 0) {
        this.localOrder.supplier = this.filteredSuppliers[0];
        this.emitUpdate();
      }
    },

    openItemBrowser() {
      this.showItemBrowserModal = true;
    },

    closeItemBrowser() {
      this.showItemBrowserModal = false;
    },

    addItemToOrder(item) {
      console.log('Adding item to order:', item);

      // Check if item already exists in order
      const existingItemIndex = this.localOrder.items.findIndex(
        (orderItem) => orderItem.item._id === item._id || orderItem.item === item._id
      );

      let updatedItems;
      const quantityToAdd = parseInt(item.addRemoveCount || item.quantity || 1, 10);

      if (existingItemIndex >= 0) {
        // Update quantity of existing item
        updatedItems = [...this.localOrder.items];
        const currentQuantity = parseInt(updatedItems[existingItemIndex].quantityOrdered || 0, 10);
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantityOrdered: currentQuantity + quantityToAdd,
        };
      } else {
        // Add new item to order
        const orderItem = {
          item: item, // Full item object (will be transformed to _id in createPayload)
          quantityOrdered: quantityToAdd,
          quantityReceived: 0, // Default for new items
          unitPrice: item.price || null,
        };

        updatedItems = [...(this.localOrder.items || []), orderItem];
      }

      // Update local order
      this.localOrder.items = updatedItems;
      this.emitUpdate();
    },

    updateOrderItems(updatedItems) {
      this.localOrder.items = updatedItems;
      this.emitUpdate();
    },

    async startOrderExportBuild() {
      if (this.localOrder?.number && this.localOrder.number !== '_new') {
        const orderNumber = this.localOrder.number;

        await this.buildSingleOrderExport({ orderNumber });
      }
    },

    async fetchOrderExportBuildState() {
      await this.fetchOrderExportAttachmentBuildState(this.localOrder.number);
      console.log('fetchOrderExportBuildState()', this.order.orderExportBuildState);
      Object.assign(this.localOrder, this.order);
      return this.order.orderExportBuildState;
    },

    async downloadOrderExport(attachmentType) {
      try {
        if (this.localOrder?.number && this.localOrder.number !== '_new') {
          // For now, we'll use the same fetchUserAttachmentUrl but with order context
          // In the future, this should be updated to use order-specific attachment URLs
          const url = await this.fetchUserAttachmentUrl({
            userId: this.getCurrentUser.id,
            attachmentType: attachmentType,
          });
          window.open(url);
        }
      } catch (error) {
        console.error('Error fetching attachment URL:', error);
      }
    },

    async onDeleteOrderAttachment(event) {
      await this.deleteOrderAttachment({
        orderId: this.localOrder._id,
        attachmentId: event.attachmentId,
        attachmentType: event.attachmentType,
      });
      // this.localOrder = this.order;
    },
  },

  async mounted() {
    await this.fetchEnumValues('OrderState');
    await this.fetchEnumValues('OrderPriority');
    await this.fetchEnumValues('OrderType');
    if (!this.projectContext) {
      await this.loadProjectData();
    }
    await this.loadSupplierData();
  },
};
</script>

<style scoped lang="scss">
.p-calendar {
  width: 100%;
}

.section-header legend {
  border-bottom: none;
}
</style>
